import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import { StyledTableRow } from "../../../../../styles/Shipmentstyles";
import { SortType } from "../../../../../models/SortType";
import {
  INVENTORY_PACK_SORT_BY,
  InventoryPackSummary,
} from "../../../../../models/inventory/InventoryAdmin";

interface InventoryDetailsPacksTableProps {
  packs: InventoryPackSummary[];
  isLoading?: boolean;
  page_no: number;
  limit: number;
  total_count: number;
  sortBy?: string;
  sortType?: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange?: (column: string, sortType: SortType) => void;
  onViewItems: (pack: InventoryPackSummary) => void;
  canManagePacks?: boolean;
  onEditPack?: (pack: InventoryPackSummary) => void;
  onEditPackItems?: (pack: InventoryPackSummary) => void;
  onDeletePack?: (pack: InventoryPackSummary) => void;
}

const getPackDateValue = (pack: InventoryPackSummary) => {
  const raw = pack.createdAt || pack.modifiedAt || "";
  if (!raw) return 0;
  const time = new Date(raw).getTime();
  return Number.isFinite(time) ? time : 0;
};

const comparePackRows = (
  a: InventoryPackSummary,
  b: InventoryPackSummary,
  column: string,
) => {
  if (column === INVENTORY_PACK_SORT_BY.PACK_NAME) {
    return (a.label || "").localeCompare(b.label || "", undefined, {
      sensitivity: "base",
    });
  }
  if (column === INVENTORY_PACK_SORT_BY.QUANTITY) {
    return (a.plannedQty ?? 0) - (b.plannedQty ?? 0);
  }
  if (column === INVENTORY_PACK_SORT_BY.DATE) {
    return getPackDateValue(a) - getPackDateValue(b);
  }
  return 0;
};

const InventoryDetailsPacksTable: React.FC<InventoryDetailsPacksTableProps> = ({
  packs,
  isLoading = false,
  page_no,
  limit,
  total_count,
  sortBy = "",
  sortType = SortType.DESC,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onViewItems,
  canManagePacks = false,
  onEditPack,
  onEditPackItems,
  onDeletePack,
}) => {
  const theme = useTheme();
  const [rowActionAnchor, setRowActionAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [activePack, setActivePack] = useState<InventoryPackSummary | null>(
    null,
  );
  const columnCount = canManagePacks ? 10 : 9;

  const sortedPacks = useMemo(() => {
    try {
      if (!sortBy) return packs;
      const direction = sortType === SortType.ASC ? 1 : -1;
      return [...packs].sort(
        (a, b) => comparePackRows(a, b, sortBy) * direction,
      );
    } catch {
      return packs;
    }
  }, [packs, sortBy, sortType]);

  const headerSortProps = onSortChange
    ? {
        sortBy,
        sortDirection: sortType,
        onSortChange,
      }
    : {};

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const handleOpenActions = (
    event: React.MouseEvent<HTMLElement>,
    pack: InventoryPackSummary,
  ) => {
    setRowActionAnchor(event.currentTarget);
    setActivePack(pack);
  };

  const handleCloseActions = () => {
    setRowActionAnchor(null);
    setActivePack(null);
  };

  return (
    <>
      <TableContainer sx={{ maxHeight: window.innerHeight - 360 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor:
                  theme.palette.customColors?.tableGrayHeadingColor,
              }}
            >
              <AppTableHeaderCell minWidth="50px" fontSize="12px">
                S.NO
              </AppTableHeaderCell>
              <AppTableHeaderCell
                minWidth="50px"
                fontSize="12px"
                sortColumn={INVENTORY_PACK_SORT_BY.PACK_NAME}
                {...headerSortProps}
              >
                PACK
              </AppTableHeaderCell>
              <AppTableHeaderCell
                minWidth="50px"
                fontSize="12px"
                sx={{ textAlign: "right" }}
              >
                ITEMS
              </AppTableHeaderCell>
              <AppTableHeaderCell
                minWidth="50px"
                fontSize="12px"
                sx={{ textAlign: "right" }}
                sortColumn={INVENTORY_PACK_SORT_BY.QUANTITY}
                {...headerSortProps}
              >
                PLANNED QTY
              </AppTableHeaderCell>
              <AppTableHeaderCell
                minWidth="50px"
                fontSize="12px"
                sx={{ textAlign: "right" }}
              >
                BUILDABLE NOW
              </AppTableHeaderCell>
              <AppTableHeaderCell
                minWidth="50px"
                fontSize="12px"
                sx={{ textAlign: "right" }}
              >
                IN FLIGHT
              </AppTableHeaderCell>
              <AppTableHeaderCell
                minWidth="50px"
                fontSize="12px"
                sx={{ textAlign: "right" }}
              >
                SHIPPED
              </AppTableHeaderCell>
              <AppTableHeaderCell minWidth="50px" fontSize="12px">
                STATUS
              </AppTableHeaderCell>
              <AppTableHeaderCell
                minWidth="140px"
                fontSize="12px"
                sortColumn={INVENTORY_PACK_SORT_BY.DATE}
                {...headerSortProps}
              >
                DATE
              </AppTableHeaderCell>
              {canManagePacks && (
                <AppTableHeaderCell minWidth="50px" fontSize="12px">
                  ACTION
                </AppTableHeaderCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoading && packs.length === 0 && (
              <StyledTableRow>
                <TableCell colSpan={columnCount}>
                  <Box py={3} textAlign="center">
                    <CustomTypography variant="body2" color="text.secondary">
                      No packs found for this inventory.
                    </CustomTypography>
                  </Box>
                </TableCell>
              </StyledTableRow>
            )}
            {sortedPacks.map((pack, index) => (
              <StyledTableRow key={pack.packId}>
                <AppTableRowCell minWidth="50px">
                  <CustomTypography variant="body2">
                    {(page_no - 1) * limit + index + 1}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell minWidth="50px">
                  <CustomTypography variant="body2" fontWeight={600}>
                    {pack.label}
                  </CustomTypography>
                  <CustomTypography variant="caption" color="text.secondary">
                    #{pack.packId}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell
                  minWidth="50px"
                  sx={{ textAlign: "right", cursor: "pointer" }}
                  onClick={() => onViewItems(pack)}
                >
                  <CustomTypography
                    variant="body2"
                    fontWeight={600}
                    color={theme.palette.customColors?.blueGradientStart}
                    sx={{ textDecoration: "underline" }}
                  >
                    {pack.productsCount}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                  <CustomTypography variant="body2">
                    {pack.plannedQty}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                  <CustomTypography
                    variant="body2"
                    fontWeight={600}
                    color={
                      pack.buildableNow < 0
                        ? theme.palette.error.main
                        : theme.palette.customColors?.text
                    }
                  >
                    {pack.buildableNow}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                  <CustomTypography variant="body2">
                    {pack.inFlight}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                  <CustomTypography variant="body2">
                    {pack.shipped}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell minWidth="50px">
                  <Chip
                    size="small"
                    label={pack.active === 1 ? "Active" : "Inactive"}
                    sx={{
                      backgroundColor:
                        pack.active === 1
                          ? theme.palette.customColors?.greenLight
                          : theme.palette.customColors?.grayBackGroundColor,
                      color: theme.palette.customColors?.text,
                      fontWeight: 600,
                    }}
                  />
                </AppTableRowCell>
                <AppTableRowCell minWidth="140px">
                  <CustomTypography variant="body2">
                    {pack.createdAt || pack.modifiedAt
                      ? formatDate(pack.createdAt || pack.modifiedAt)
                      : "—"}
                  </CustomTypography>
                </AppTableRowCell>
                {canManagePacks && (
                  <AppTableRowCell minWidth="50px">
                    <Button
                      variant="outlined"
                      onClick={(event) => handleOpenActions(event, pack)}
                      sx={{
                        minWidth: "94px",
                        height: "32px",
                        fontSize: "0.8rem",
                        textTransform: "none",
                        borderColor: theme.palette.customColors?.greyBorder,
                        color: theme.palette.customColors?.text,
                      }}
                    >
                      Actions
                    </Button>
                  </AppTableRowCell>
                )}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[30, 50, 100, 150, 200]}
        component="div"
        count={total_count}
        rowsPerPage={limit}
        page={Math.max(page_no - 1, 0)}
        onPageChange={(event, nextPage) => onPageChange(event, nextPage + 1)}
        onRowsPerPageChange={(event) =>
          handleRowsChange(parseInt(event.target.value, 10))
        }
      />
      {canManagePacks && (
        <Menu
          anchorEl={rowActionAnchor}
          open={Boolean(rowActionAnchor)}
          onClose={handleCloseActions}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <MenuItem
            onClick={() => {
              if (activePack) onEditPack?.(activePack);
              handleCloseActions();
            }}
          >
            <CustomTypography variant="body2" fontWeight={600}>
              Edit Pack
            </CustomTypography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (activePack) onEditPackItems?.(activePack);
              handleCloseActions();
            }}
          >
            <CustomTypography variant="body2" fontWeight={600}>
              Edit Items
            </CustomTypography>
          </MenuItem>
          <MenuItem
            disabled={!activePack || activePack.active !== 1}
            onClick={() => {
              if (activePack) onDeletePack?.(activePack);
              handleCloseActions();
            }}
          >
            <CustomTypography variant="body2" fontWeight={600}>
              Delete
            </CustomTypography>
          </MenuItem>
        </Menu>
      )}
    </>
  );
};

export default observer(InventoryDetailsPacksTable);
